/**
 * Prevents raw system-level error messages from reaching the customer.
 *
 * Gateway and API errors flow through multiple paths in the checkout
 * (VGS 3DS, PayPal, Braintree, redirect payments). Each path should
 * funnel error text through this function before calling setPaymentError
 * or setCheckoutError.
 */

// HTTP status texts — matched as the full trimmed message only,
// so "Payment account not found" won't false-match "not found".
const EXACT_SYSTEM_MESSAGES = new Set([
  "unprocessable entity",
  "internal server error",
  "bad request",
  "not found",
  "service unavailable",
  "gateway timeout",
  "bad gateway",
  "unauthorized",
  "forbidden",
  "method not allowed",
  "request timeout",
  "too many requests",
]);

// JS runtime errors, network errors, and low-level system phrases —
// matched as substrings since they appear inside longer error messages.
const SYSTEM_SUBSTRINGS = [
  "connection refused",
  "network error",
  "failed to fetch",
  "load failed",
  "script error",
  "typeerror",
  "referenceerror",
  "syntaxerror",
  "rangeerror",
  "cannot read propert", // intentionally truncated — catches both "property" and "properties"
  "undefined is not",
  "null is not",
  "is not a function",
  "is not defined",
  "unexpected token",
  "unexpected end",
  "json.parse",
  "json parse error",
  "econnrefused",
  "econnreset",
  "etimedout",
];

const DEFAULT_PAYMENT_ERROR =
  "Payment failed. Please try again or use a different payment method.";

/**
 * Returns the message if it looks customer-friendly, otherwise a safe fallback.
 *
 * @param message  - The raw error string (may be undefined/null/empty).
 * @param fallback - Optional custom fallback. Defaults to a generic payment error.
 */
export function sanitizeErrorMessage(
  message: string | undefined | null,
  fallback: string = DEFAULT_PAYMENT_ERROR,
): string {
  if (!message || message.trim().length === 0) return fallback;

  const lower = message.toLowerCase().trim();

  if (EXACT_SYSTEM_MESSAGES.has(lower)) return fallback;
  if (SYSTEM_SUBSTRINGS.some((phrase) => lower.includes(phrase)))
    return fallback;

  return message;
}
